import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { useConfirmation } from '@/contexts/confirmation'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { paths } from '@/utils/path'
import { getCVs, deleteCV, exportCVs } from '../../services'
import { Cv } from '../../types'
import {
    getResumeColumns,
    getResumeActions,
    getResumeFilters,
} from './resume-list-config'
import { useResumeListTour } from './use-resume-list-tour'
import { useResumeListQuickHelp } from './use-resume-list-quick-help'

interface ResumeFilters {
    job_title: string
    cover_letter_source_name: string
    department: string
    created_at: string[] | null
}

/**
 * Business logic hook for Resume List
 */
export const useResumeList = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { setConfirmation } = useConfirmation()

    // URL params & filters
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<ResumeFilters>({
        defaults: { sort_by: 'id', sort_order: 'desc' }, // Match API default
        filterDefaults: {
            job_title: '',
            cover_letter_source_name: '',
            department: '',
            created_at: null,
        },
    })

    // Form for filter controls
    const { control } = useForm<ResumeFilters>({
        defaultValues: filters,
    })

    // Fetch resumes data
    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.RESUMES, queryParams],
        queryFn: () => getCVs(queryParams),
        placeholderData: true,
    })

    // Delete mutation
    const { mutateAsync: deleteCVMutation } = useMutationWithHandler(
        { mutationFn: deleteCV },
        { reactQueryKeys: QUERY_KEYS.RESUMES }
    )

    // Handle edit
    const handleEdit = useCallback(
        (row: Cv) => {
            navigate(`/resume/${row?.id}`)
        },
        [navigate]
    )

    // Handle delete with confirmation
    const handleDelete = useCallback(
        (row: Cv) => {
            setConfirmation({
                title: t('cv-title-delete'),
                description: `${t('cv-description-delete')} "${row.name}"?`,
                onConfirm: async () => {
                    try {
                        await deleteCVMutation(row.id)
                        toast.success(`${t('cv-delete-success')} ${row.name}`)
                    } catch {
                        toast.error(`${t('cv-delete-failed')} ${row.name}`)
                    }
                },
                open: true,
            })
        },
        [t, setConfirmation, deleteCVMutation]
    )

    // Handle export
    const handleExport = useCallback(async () => {
        try {
            const payload = {
                page: 1,
                page_size: 99999,
                q: '',
                filters: queryParams.filters || '',
            }
            const response = await exportCVs(payload)

            const url = window.URL.createObjectURL(new Blob([response]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute(
                'download',
                `cvs_${new Date().toISOString()}.xlsx`
            )
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)

            toast.success(t('dowload-success'))
        } catch (error) {
            console.error('Export error:', error)
            toast.error('Đã xảy ra lỗi khi xuất file Excel.')
        }
    }, [queryParams.filters, t])

    // Memoized configs
    const columns = useMemo(() => getResumeColumns(t), [t])

    const actions = useMemo(
        () =>
            getResumeActions({
                t,
                onEdit: handleEdit,
                onDelete: handleDelete,
            }),
        [t, handleEdit, handleDelete]
    )

    const filterItems = useMemo(() => getResumeFilters(t), [t])

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    const handleCreateNew = useCallback(() => {
        navigate(paths.talentPoolCreate)
    }, [navigate])

    const handleManageJobs = useCallback(() => {
        navigate(paths.jobList)
    }, [navigate])

    // Tour and Quick Help
    const tour = useResumeListTour()
    useResumeListQuickHelp()

    return {
        // Data
        resumes: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),

        // Params & filters
        params,
        filters,
        queryParams,
        control,
        setFilter,
        searchInputProps,

        // Configs
        columns,
        actions,
        filterItems,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        handleCreateNew,
        handleManageJobs,
        handleExport,
        refetch,

        // Tour
        tour,

        // Translation
        t,
    }
}
